import {
  ApiBlueprint,
  createApiFactory,
  createFrontendPlugin,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/frontend-plugin-api';
import {
  EntityCardBlueprint,
  EntityContentBlueprint,
} from '@backstage/plugin-catalog-react/alpha';
import {
  ArgocdDiscoveryApi,
  argocdDiscoveryApiRef,
} from './ArgocdDiscoveryApi';

/**
 * Argo CD deployment views for catalog services.
 *
 * The community Argo CD plugin only shows applications an entity names in its
 * annotations, which would mean annotating every service and every new one.
 * PayIt's ApplicationSets already name applications `<service>-<cluster>`, so
 * like the Prometheus module these views derive their lookup from the entity
 * itself and cover every service by default.
 */

const argocdDiscoveryApi = ApiBlueprint.make({
  name: 'discovery-api',
  params: defineParams =>
    defineParams(
      createApiFactory({
        api: argocdDiscoveryApiRef,
        deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
        factory: deps => new ArgocdDiscoveryApi(deps),
      }),
    ),
});

/** Services are the entities Argo CD deploys; websites and tools opt in later. */
const isService = { kind: 'Component', 'spec.type': 'service' };

export const argocdEntityContent = EntityContentBlueprint.make({
  name: 'content',
  params: {
    path: '/argocd',
    title: 'Argo CD',
    filter: isService,
    loader: async () => {
      const { compatWrapper } = await import('@backstage/core-compat-api');
      const { ServiceDeploymentsContent } = await import(
        './ServiceDeploymentsContent'
      );
      return compatWrapper(<ServiceDeploymentsContent />);
    },
  },
});

export const argocdEntityCard = EntityCardBlueprint.make({
  name: 'summary',
  params: {
    // A wide table rather than a sidebar summary, so it goes in the main column.
    type: 'content',
    filter: isService,
    loader: async () => {
      const { compatWrapper } = await import('@backstage/core-compat-api');
      const { ServiceDeploymentsCard } = await import(
        './ServiceDeploymentsCard'
      );
      return compatWrapper(<ServiceDeploymentsCard />);
    },
  },
});

export default createFrontendPlugin({
  pluginId: 'argocd',
  extensions: [argocdDiscoveryApi, argocdEntityContent, argocdEntityCard],
});
